import { useState } from 'react'
import { api } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, Modal, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'

export default function Network () {
  const { data, error, loading, reload } = useAsync(() => api.get('/admin/network'), [], { live: ['network'] })
  const toast = useToast()
  const confirm = useConfirm()
  const [adding, setAdding] = useState(null) // 'vehicle' | 'liaison'
  const [busy, setBusy] = useState(false)
  const [vehicle, setVehicle] = useState({ regNo: '', driverName: '', driverPhone: '', ownerNetwork: '', capacityBags: 200 })
  const [liaison, setLiaison] = useState({ name: '', phone: '', baseLocation: '', username: '', password: '' })

  const submitVehicle = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Add this vehicle to the network?',
      body: 'It becomes available for the desk to assign to trips. Only put pre-verified vehicles on this list.',
      facts: [
        ['Registration', (vehicle.regNo || '').toUpperCase()],
        ['Driver', vehicle.driverName],
        ['Phone', vehicle.driverPhone],
        ['Capacity', `${vehicle.capacityBags} bags`]
      ],
      confirmLabel: 'Add vehicle'
    })
    if (!ok) return
    setBusy(true)
    try {
      await api.post('/admin/network/vehicles', vehicle)
      toast.ok(`${vehicle.regNo.toUpperCase()} added to the pre-verified network.`)
      setVehicle({ regNo: '', driverName: '', driverPhone: '', ownerNetwork: '', capacityBags: 200 })
      setAdding(null)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  const submitLiaison = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Add this liaison officer?',
      body: 'A field login is issued at the same time. They can be assigned trips as soon as it exists, and the handover OTP runs on their phone.',
      facts: [['Name', liaison.name], ['Phone', liaison.phone], ['Base', liaison.baseLocation || 'not stated']],
      confirmLabel: 'Add officer & issue login'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post('/admin/network/liaisons', liaison)
      toast.ok(`${liaison.name} added. Field login: ${res.credentials.username} / ${res.credentials.password}`)
      setLiaison({ name: '', phone: '', baseLocation: '', username: '', password: '' })
      setAdding(null)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  const toggleVehicle = async v => {
    const off = v.active
    const ok = await confirm({
      title: off ? `Take ${v.regNo} off the network?` : `Put ${v.regNo} back on the network?`,
      body: off
        ? 'It stops being offered when the desk assigns a vehicle. Trips already assigned to it are not affected.'
        : 'It becomes available again when the desk assigns a vehicle.',
      facts: [['Driver', v.driverName], ['Phone', v.driverPhone]],
      confirmLabel: off ? 'Take off network' : 'Put back on',
      tone: off ? 'danger' : ''
    })
    if (!ok) return
    try {
      await api.post(`/admin/network/vehicles/${v._id}/active`, { active: v.active ? 0 : 1 })
      reload()
    } catch (err) { toast.error(err.message) }
  }

  const toggleLiaison = async l => {
    const off = l.active
    const ok = await confirm({
      title: off ? `Deactivate ${l.name}?` : `Reactivate ${l.name}?`,
      body: off
        ? 'Their field login is disabled and they stop being offered for new trips. Trips already assigned stay with them.'
        : 'Their field login works again and they can be assigned new trips.',
      facts: [['Phone', l.phone], ['Login', l.login?.username || 'none issued']],
      confirmLabel: off ? 'Deactivate officer' : 'Reactivate',
      tone: off ? 'danger' : ''
    })
    if (!ok) return
    try {
      await api.post(`/admin/network/liaisons/${l._id}/active`, { active: l.active ? 0 : 1 })
      reload()
    } catch (err) { toast.error(err.message) }
  }

  if (loading) return <Page title="Transport network"><Spinner /></Page>
  if (error) return <Page title="Transport network"><ErrorBox error={error} onRetry={reload} /></Page>

  return (
    <Page
      title="Transport network"
      subtitle="The platform owns no trucks. Seed moves on pre-verified third-party pickups, and a platform liaison officer rides with every load from the hatchery gate to the pond."
    >
      <div className="split">
        <Card title={`Pre-verified vehicles (${data.vehicles.length})`} padded={false}
          actions={<button className="btn sm primary" onClick={() => setAdding('vehicle')}>Add vehicle</button>}>
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Vehicle</th><th>Driver</th><th>Network</th><th className="num">Bags</th><th>Oxygen kit</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {data.vehicles.map(v => (
                  <tr key={v._id}>
                    <td className="mono">{v.regNo}</td>
                    <td>{v.driverName}<span className="t-sub">{v.driverPhone}</span></td>
                    <td className="small">{v.ownerNetwork || '—'}</td>
                    <td className="num">{v.capacityBags}</td>
                    <td>{v.hasOxygenKit ? <Badge tone="good">Fitted</Badge> : <Badge tone="warn">No</Badge>}</td>
                    <td><Badge tone={v.active ? 'good' : 'muted'}>{v.active ? 'Active' : 'Off roster'}</Badge></td>
                    <td className="right"><button className="btn sm" onClick={() => toggleVehicle(v)}>{v.active ? 'Stand down' : 'Activate'}</button></td>
                  </tr>
                ))}
                {!data.vehicles.length && <tr><td colSpan={7}><Empty>No vehicles in the network yet.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title={`Liaison officers (${data.liaisons.length})`} padded={false}
          actions={<button className="btn sm primary" onClick={() => setAdding('liaison')}>Add officer</button>}>
          <div className="scroll-x">
            <table>
              <thead>
                <tr><th>Officer</th><th>Base</th><th>Field login</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {data.liaisons.map(l => (
                  <tr key={l._id}>
                    <td>{l.name}<span className="t-sub">{l.phone}</span></td>
                    <td>{l.baseLocation || '—'}</td>
                    <td className="mono small">{l.login?.username || '—'}</td>
                    <td><Badge tone={l.active ? 'good' : 'muted'}>{l.active ? 'On duty' : 'Off duty'}</Badge></td>
                    <td className="right"><button className="btn sm" onClick={() => toggleLiaison(l)}>{l.active ? 'Stand down' : 'Activate'}</button></td>
                  </tr>
                ))}
                {!data.liaisons.length && <tr><td colSpan={5}><Empty>No liaison officers yet.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {adding === 'vehicle' && (
        <Modal title="Add a pre-verified vehicle" onClose={() => setAdding(null)}>
          <form onSubmit={submitVehicle}>
            <div className="formgrid">
              <Field label="Registration number" required>
                <input value={vehicle.regNo} onChange={e => setVehicle({ ...vehicle, regNo: e.target.value })} placeholder="AP39TX4412" required />
              </Field>
              <Field label="Driver name" required>
                <input value={vehicle.driverName} onChange={e => setVehicle({ ...vehicle, driverName: e.target.value })} required />
              </Field>
              <Field label="Driver phone" required>
                <input value={vehicle.driverPhone} onChange={e => setVehicle({ ...vehicle, driverPhone: e.target.value })} required />
              </Field>
              <Field label="Owner network">
                <input value={vehicle.ownerNetwork} onChange={e => setVehicle({ ...vehicle, ownerNetwork: e.target.value })} placeholder="Kakinada Aqua Carriers" />
              </Field>
              <Field label="Capacity (oxygen bags)">
                <input type="number" value={vehicle.capacityBags} onChange={e => setVehicle({ ...vehicle, capacityBags: e.target.value })} />
              </Field>
            </div>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit">Add vehicle</SubmitButton>
              <button type="button" className="btn" onClick={() => setAdding(null)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {adding === 'liaison' && (
        <Modal title="Add a liaison officer" onClose={() => setAdding(null)}>
          <form onSubmit={submitLiaison}>
            <p className="muted small" style={{ marginTop: 0 }}>
              A field login is created at the same time. The officer uses it on a phone to log packing and capture the delivery OTP.
            </p>
            <div className="formgrid">
              <Field label="Name" required>
                <input value={liaison.name} onChange={e => setLiaison({ ...liaison, name: e.target.value })} required />
              </Field>
              <Field label="Phone" required>
                <input value={liaison.phone} onChange={e => setLiaison({ ...liaison, phone: e.target.value })} required />
              </Field>
              <Field label="Base location">
                <input value={liaison.baseLocation} onChange={e => setLiaison({ ...liaison, baseLocation: e.target.value })} placeholder="Kakinada" />
              </Field>
              <Field label="Username" hint="Blank = derived from the name.">
                <input value={liaison.username} onChange={e => setLiaison({ ...liaison, username: e.target.value })} autoComplete="off" />
              </Field>
              <Field label="Password" hint="Blank = generated automatically.">
                <input value={liaison.password} onChange={e => setLiaison({ ...liaison, password: e.target.value })} autoComplete="off" />
              </Field>
            </div>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit">Add officer</SubmitButton>
              <button type="button" className="btn" onClick={() => setAdding(null)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
